import { useEffect, useState } from 'react';
import { apiCall } from '../services/api.service';

/**
 * The caller's own handle, fetched once per page load and shared.
 *
 * Four places show the signed-in person's name — the profile page, the mobile
 * profile, the mobile layout header, the Process Studio greeting — and each of
 * them wants the handle as a fallback when there is no name. Without a shared
 * cache that is four identical requests on every navigation, which is how a
 * display helper turns into a network problem.
 *
 * A module-level promise rather than a store: the value is immutable for the
 * life of the session (a handle is claim-once, and an admin change lands on a
 * reload anyway), so there is nothing to invalidate and nothing to subscribe
 * to. It resets naturally when the page does.
 *
 * Failure is silent by design. A missing handle is not an error — most people
 * will not have claimed one — and the display helper falls back to the email
 * exactly as it did before.
 */

let cached: Promise<string | null> | null = null;

function loadOnce(): Promise<string | null> {
  if (!cached) {
    cached = apiCall('handles/me', 'GET', null, false, { silentError: true })
      .then((data) => data?.primary || null)
      .catch(() => null);
  }
  return cached;
}

/** Test seam, and the reset a sign-out needs so the next person is not shown
 *  the previous one's handle. */
export function resetMyHandleCache(): void {
  cached = null;
}

export function useMyHandle(): string | null {
  const [handle, setHandle] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    loadOnce().then((value) => {
      if (!cancelled) setHandle(value);
    });
    return () => { cancelled = true; };
  }, []);

  return handle;
}

export default useMyHandle;
